import axios from 'axios';
import { withBrowserTimezoneHeaders } from '../utils/browserTimezone';

// AI Hub base - Uses environment variables for all environments (dev/staging/prod)
// Falls back to BACKEND_URL (Gateway) if AI_HUB_BASE_URL not set
// Same pattern as all other components: Login.jsx, SyncFlow.jsx, etc.
const AI_HUB_BASE_URL = process.env.REACT_APP_AI_HUB_BASE_URL || process.env.REACT_APP_BACKEND_URL || '';
const CHAT_ENDPOINT = `${AI_HUB_BASE_URL}asrosync/chat`;

/**
 * Send a message to the AI agent (n8n webhook)
 * @param {string} message - The user's message
 * @param {Object} [options]
 * @param {boolean} [options.strictCurrentQuery] - When true, LangChain runs without prior chat/vector context
 * @returns {Promise<Object>} - The response from the AI agent
 */
export const sendMessage = async (message, options = {}) => {
  try {
    const token = localStorage.getItem('token');
    const tenantId = localStorage.getItem('tenantId') || localStorage.getItem('tenant_id');
    const userId = localStorage.getItem('userId');

    // Prepare headers
    const headers = {
      'Content-Type': 'application/json',
    };

    // Add authentication if token exists (from your app)
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Add tenant/user info if available
    if (tenantId) {
      headers['X-Tenant-Id'] = tenantId;
    }
    if (userId) {
      headers['X-User-Id'] = userId;
    }

    Object.assign(headers, withBrowserTimezoneHeaders({}));

    // Prepare request body for n8n "When chat message received" trigger
    // n8n chat trigger expects a plain object with:
    // - sessionId: 32-character lowercase hex string
    // - action: "sendMessage"
    // - chatInput: the user's message

    // Get or create sessionId for maintaining conversation context
    // Store in localStorage so it persists across page refreshes
    // Format: 32-character lowercase hexadecimal string (e.g., "6a40b7e4756647578cfe17138949200f")
    let sessionId = localStorage.getItem('n8n_chat_sessionId');
    if (!sessionId) {
      // Generate a new sessionId in n8n format: 32 lowercase hex characters
      const randomBytes = crypto.getRandomValues(new Uint8Array(16));
      sessionId = Array.from(randomBytes)
        .map(byte => byte.toString(16).padStart(2, '0'))
        .join('')
        .toLowerCase(); // Ensure lowercase
      localStorage.setItem('n8n_chat_sessionId', sessionId);
    }

    // n8n expects plain object structure
    const requestBody = {
      sessionId: sessionId,
      message,
    };
    if (options.strictCurrentQuery === true) {
      requestBody.strictCurrentQuery = true;
    }

    if (!CHAT_ENDPOINT) {
      throw new Error('AI Hub chat endpoint is not configured. Set REACT_APP_AI_HUB_BASE_URL or ensure gateway routing for /asrosync.');
    }

    console.log('📤 Sending message to AI Hub chat:', CHAT_ENDPOINT);
    const response = await axios.post(CHAT_ENDPOINT, requestBody, {
      headers,
      timeout: 180000, // 180 second timeout (3 minutes) - Enterprise SQL agent with multiple schema checks, query validation, and email sending can take longer
    });

    // Handle AI Hub normalized response formats
    if (response.data) {
      if (response.data.message) {
        return {
          message: response.data.message,
          requestId: response.data.requestId || response.data.request_id || null,
        };
      }
      if (typeof response.data === 'string') {
        return { message: response.data };
      }
      console.warn('Unexpected response format:', response.data);
      return { message: JSON.stringify(response.data) };
    }

    throw new Error('No response data received');
  } catch (error) {
    console.error('Error in sendMessage:', error);

    // Handle specific error cases
    if (error.response) {
      // Server responded with error status
      throw new Error(
        error.response.data?.message ||
        error.response.data?.error ||
        `Server error: ${error.response.status}`
      );
    } else if (error.request) {
      // Request was made but no response received
      throw new Error('No response from server. Please check your connection.');
    } else {
      // Something else happened
      throw new Error(error.message || 'An unexpected error occurred');
    }
  }
};

/**
 * Send a message with streaming (SSE). Calls onChunk(delta) for each message chunk,
 * onDone({ sessionId }) when stream ends, or throws on error.
 * @param {string} message - The user's message
 * @param {Object} handlers - { onChunk, onDone, strictCurrentQuery? }
 * @returns {Promise<void>}
 */
export const sendMessageStreaming = async (
  message,
  { onChunk, onDone, onReplace, strictCurrentQuery, listPaginationAction, pageSize } = {}
) => {
  const token = localStorage.getItem('token');
  const tenantId = localStorage.getItem('tenantId') || localStorage.getItem('tenant_id');
  const userId = localStorage.getItem('userId');

  let sessionId = localStorage.getItem('n8n_chat_sessionId');
  if (!sessionId) {
    const randomBytes = crypto.getRandomValues(new Uint8Array(16));
    sessionId = Array.from(randomBytes)
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('')
      .toLowerCase();
    localStorage.setItem('n8n_chat_sessionId', sessionId);
  }

  const url = CHAT_ENDPOINT ? `${CHAT_ENDPOINT}?stream=true` : '';
  if (!url) {
    throw new Error('AI Hub chat endpoint is not configured. Set REACT_APP_AI_HUB_BASE_URL or ensure gateway routing for /asrosync.');
  }

  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (tenantId) headers['X-Tenant-Id'] = tenantId;
  if (userId) headers['X-User-Id'] = userId;
  Object.assign(headers, withBrowserTimezoneHeaders({}));

  const timeoutMs = 180000;
  const requestStart = performance.now();
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      sessionId,
      message,
      ...(strictCurrentQuery === true ? { strictCurrentQuery: true } : {}),
      ...(listPaginationAction ? { list_pagination_action: listPaginationAction } : {}),
      ...(pageSize != null ? { page_size: pageSize } : {}),
    }),
    signal: controller.signal,
  }).finally(() => clearTimeout(timeoutId));

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || err.error || `Server error: ${res.status}`);
  }

  const contentType = res.headers.get('Content-Type') || '';
  if (!contentType.includes('text/event-stream')) {
    const data = await res.json().catch(() => ({}));
    if (data.message) {
      onChunk(data.message);
      onDone({
        sessionId: data.sessionId || sessionId,
        requestId: data.request_id || data.requestId || null,
        list_pagination_state: data.list_pagination_state || null,
        list_pagination_update: Boolean(data.list_pagination_update),
      });
      return;
    }
    throw new Error(data.message || data.error || 'Unexpected response format');
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    for (const line of lines) {
      if (line.startsWith('data: ')) {
        const raw = line.slice(6).trim();
        if (!raw) continue;
        try {
          const data = JSON.parse(raw);
          if (data.error) throw new Error(data.error);
          if (data.status) continue;
          if (data.done) {
            onDone({
              sessionId: data.sessionId || sessionId,
              requestId: data.request_id || data.requestId || null,
              execution_time_ms: Math.round(performance.now() - requestStart),
              list_pagination_state: data.list_pagination_state ?? null,
              list_pagination_update: Boolean(data.list_pagination_update),
            });
            return;
          }
          if (data.replace != null) {
            if (onReplace) onReplace(String(data.replace));
            continue;
          }
          if (data.message != null) onChunk(data.message);
        } catch (e) {
          if (e instanceof SyntaxError) continue;
          throw e;
        }
      }
    }
  }

  onDone({
    sessionId,
    requestId: null,
    execution_time_ms: Math.round(performance.now() - requestStart),
    list_pagination_state: null,
    list_pagination_update: false,
  });
};

/**
 * Generate a new session id and store it in localStorage (New Chat).
 * @returns {string}
 */
export const createNewSessionId = () => {
  const randomBytes = crypto.getRandomValues(new Uint8Array(16));
  const sessionId = Array.from(randomBytes)
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('')
    .toLowerCase();
  localStorage.setItem('n8n_chat_sessionId', sessionId);
  return sessionId;
};

/**
 * Load persisted messages for a thread from the AI Hub History API.
 * @param {string|null} sessionId - Thread id (defaults to localStorage n8n_chat_sessionId)
 * @param {number} limit - Max messages (default 200)
 * @returns {Promise<Array<{ role: string, content: string, created_at?: string }>>}
 */
export const getChatHistory = async (sessionId = null, limit = 200) => {
  const threadId = sessionId || localStorage.getItem('n8n_chat_sessionId');
  if (!threadId) {
    return [];
  }

  const token = localStorage.getItem('token');
  const tenantId = localStorage.getItem('tenantId') || localStorage.getItem('tenant_id');
  const userId = localStorage.getItem('userId');

  const headers = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  if (tenantId) {
    headers['X-Tenant-Id'] = tenantId;
  }
  if (userId) {
    headers['X-User-Id'] = userId;
  }
  Object.assign(headers, withBrowserTimezoneHeaders({}));

  if (!CHAT_ENDPOINT) {
    throw new Error('AI Hub chat endpoint is not configured. Set REACT_APP_AI_HUB_BASE_URL or ensure gateway routing for /asrosync.');
  }

  const url = `${CHAT_ENDPOINT}/sessions/${encodeURIComponent(threadId)}/messages`;

  try {
    const response = await axios.get(url, {
      headers,
      params: { limit },
      timeout: 60000,
    });
    const data = response.data;
    if (data?.ok === true && Array.isArray(data.messages)) {
      return data.messages;
    }
    if (data?.ok === false) {
      throw new Error(data.message || 'Failed to load chat history');
    }
    return [];
  } catch (error) {
    console.error('Error in getChatHistory:', error);
    if (error.response) {
      throw new Error(
        error.response.data?.message ||
        error.response.data?.error ||
        `Server error: ${error.response.status}`
      );
    }
    if (error.request) {
      throw new Error('No response from server. Please check your connection.');
    }
    throw new Error(error.message || 'An unexpected error occurred');
  }
};

/**
 * Load persisted chat sessions for the sidebar.
 * @param {number} limit - Max sessions (default 50)
 * @returns {Promise<Array<{ threadId: string, title: string, lastMessageAt?: string, startedAt?: string, messageCount?: number }>>}
 */
export const getChatSessions = async (limit = 50) => {
  const token = localStorage.getItem('token');
  const tenantId = localStorage.getItem('tenantId') || localStorage.getItem('tenant_id');
  const userId = localStorage.getItem('userId');

  const headers = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  if (tenantId) {
    headers['X-Tenant-Id'] = tenantId;
  }
  if (userId) {
    headers['X-User-Id'] = userId;
  }
  Object.assign(headers, withBrowserTimezoneHeaders({}));

  if (!CHAT_ENDPOINT) {
    throw new Error('AI Hub chat endpoint is not configured. Set REACT_APP_AI_HUB_BASE_URL or ensure gateway routing for /asrosync.');
  }

  const url = `${CHAT_ENDPOINT}/sessions`;

  try {
    const response = await axios.get(url, {
      headers,
      params: { limit },
      timeout: 60000,
    });
    const data = response.data;
    if (data?.ok === true && Array.isArray(data.sessions)) {
      return data.sessions;
    }
    if (data?.ok === false) {
      throw new Error(data.message || 'Failed to load chat sessions');
    }
    return [];
  } catch (error) {
    console.error('Error in getChatSessions:', error);
    if (error.response) {
      throw new Error(
        error.response.data?.message ||
        error.response.data?.error ||
        `Server error: ${error.response.status}`
      );
    }
    if (error.request) {
      throw new Error('No response from server. Please check your connection.');
    }
    throw new Error(error.message || 'An unexpected error occurred');
  }
};

/**
 * Rename a chat session (custom display title).
 * @param {string} threadId
 * @param {string} title
 * @returns {Promise<{ threadId: string, title: string }>}
 */
export const renameChatSession = async (threadId, title) => {
  if (!threadId) {
    throw new Error('threadId is required');
  }
  const trimmed = title?.trim();
  if (!trimmed) {
    throw new Error('title is required');
  }

  const token = localStorage.getItem('token');
  const tenantId = localStorage.getItem('tenantId') || localStorage.getItem('tenant_id');
  const userId = localStorage.getItem('userId');

  const headers = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  if (tenantId) {
    headers['X-Tenant-Id'] = tenantId;
  }
  if (userId) {
    headers['X-User-Id'] = userId;
  }
  Object.assign(headers, withBrowserTimezoneHeaders({}));

  if (!CHAT_ENDPOINT) {
    throw new Error('AI Hub chat endpoint is not configured. Set REACT_APP_AI_HUB_BASE_URL or ensure gateway routing for /asrosync.');
  }

  const url = `${CHAT_ENDPOINT}/sessions/${encodeURIComponent(threadId)}`;

  try {
    const response = await axios.patch(url, { title: trimmed }, {
      headers,
      timeout: 60000,
    });
    const data = response.data;
    if (data?.ok === true) {
      return {
        threadId: data.threadId || threadId,
        title: data.title || trimmed,
      };
    }
    throw new Error(data?.message || 'Failed to rename chat session');
  } catch (error) {
    console.error('Error in renameChatSession:', error);
    if (error.response) {
      throw new Error(
        error.response.data?.message ||
        error.response.data?.error ||
        `Server error: ${error.response.status}`
      );
    }
    if (error.request) {
      throw new Error('No response from server. Please check your connection.');
    }
    throw new Error(error.message || 'An unexpected error occurred');
  }
};

/**
 * Delete a chat session and all its persisted messages.
 * @param {string} threadId
 * @returns {Promise<{ threadId: string }>}
 */
export const deleteChatSession = async (threadId) => {
  if (!threadId) {
    throw new Error('threadId is required');
  }

  const token = localStorage.getItem('token');
  const tenantId = localStorage.getItem('tenantId') || localStorage.getItem('tenant_id');
  const userId = localStorage.getItem('userId');

  const headers = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  if (tenantId) {
    headers['X-Tenant-Id'] = tenantId;
  }
  if (userId) {
    headers['X-User-Id'] = userId;
  }
  Object.assign(headers, withBrowserTimezoneHeaders({}));

  if (!CHAT_ENDPOINT) {
    throw new Error('AI Hub chat endpoint is not configured. Set REACT_APP_AI_HUB_BASE_URL or ensure gateway routing for /asrosync.');
  }

  const url = `${CHAT_ENDPOINT}/sessions/${encodeURIComponent(threadId)}`;

  try {
    const response = await axios.delete(url, {
      headers,
      timeout: 60000,
    });
    const data = response.data;
    if (data?.ok === true) {
      return { threadId: data.threadId || threadId };
    }
    throw new Error(data?.message || 'Failed to delete chat session');
  } catch (error) {
    console.error('Error in deleteChatSession:', error);
    if (error.response) {
      throw new Error(
        error.response.data?.message ||
        error.response.data?.error ||
        `Server error: ${error.response.status}`
      );
    }
    if (error.request) {
      throw new Error('No response from server. Please check your connection.');
    }
    throw new Error(error.message || 'An unexpected error occurred');
  }
};

